<script setup lang="ts">
import { useCmsTranslations } from "@shopware/composables";
import { getCmsTranslate } from "@shopware/helpers";
import { defu } from "defu";
import { toRefs } from "vue";
import {
  useAddToCart,
  useCartErrorParamsResolver,
  useCartNotification,
  useNotifications,
} from "#imports";
import type { operations, Schemas } from "#shopware";
import { addToCartTracking } from "~/components/utilities/GtmTrackings";

const { pushSuccess, pushError } = useNotifications();
const { getErrorsCodes } = useCartNotification();
const { resolveCartError } = useCartErrorParamsResolver();
const props = defineProps<{
  product: Schemas["Product"];
}>();

type Translations = {
  product: {
    messages: {
      addedToCart: string;
    };
    qty: string;
    addToCart: string;
  };
  errors: {
    [key: string]: string;
  };
};

let translations: Translations = {
  product: {
    messages: {
      addedToCart: "has been added to cart.",
    },
    qty: "Qty",
    addToCart: "In den Warenkorb",
  },
  errors: {
    "product-stock-reached":
      "The product {name} is only available {quantity} times",
  },
};

translations = defu(useCmsTranslations(), translations) as Translations;

const { t } = useI18n();
const { product } = toRefs(props);
const { addToCart, quantity } = useAddToCart(product);
const { addProducts } = useCart();
const { itemsToCart, getAssignedProductData } = useProductSetConfiguration();

const addToCartProxy = async () => {
  await addToCart();
  const errors = getErrorsCodes();
  for (const element of errors) {
    const { messageKey, params } = resolveCartError(element);
    pushError(getCmsTranslate(translations.errors[messageKey], params));
  }

  if (!errors.length)
    pushSuccess(
      t("product.messages.addedToCart", { p: props.product?.translated.name })
    );

  addToCartTracking(product.value, quantity);

  const items: operations["addLineItem post /checkout/cart/line-item"]["body"]["items"] =
    itemsToCart.value.map((item) => ({
      id: item.productId,
      quantity:
        getAssignedProductData(item.productId)?.selectedQuantity ||
        getAssignedProductData(item.productId)?.accessoryQuantity,
      type: "product",
    }));

  await Promise.all(
    items.map((item) => addToCartTracking(item, item.quantity))
  );

  await addProducts(items);
};

const minPurchase = computed(() => product.value.minPurchase || 1);
const maxPurchase = computed(
  () => product.value.calculatedMaxPurchase || 10000
);

quantity.value = minPurchase.value;

const increaseQuantity = () => {
  if (quantity.value < maxPurchase.value) {
    quantity.value += product.value?.purchaseSteps || 1;
  }
};

const decreaseQuantity = () => {
  if (quantity.value > minPurchase.value) {
    quantity.value -= product.value?.purchaseSteps || 1;
  }
};
</script>

<template>
  <div class="flex my-6 justify-between">
    <div
      class="flex items-center gap-4 bg-gray-100 rounded-full px-4 py-2 h-14 md:h-16 bg-cust_cream w-[110px] md:w-32 font-redit_sans_con text-xl md:text-28px text-cust_gray"
    >
      <button
        class="decrement font-semibold focus:outline-none hover:text-gray-700 w-1/3 h-full"
        @click="decreaseQuantity"
        :disabled="quantity <= minPurchase"
      >
        −
      </button>
      <span class="font-medium w-1/3 text-center counter">{{ quantity }}</span>
      <button
        class="increment font-semibold focus:outline-none hover:text-gray-700 w-1/3 h-full"
        @click="increaseQuantity"
        :disabled="quantity >= maxPurchase"
      >
        +
      </button>
    </div>
    <button
      :disabled="!product.available"
      class="w-[calc(100%-120px)] md:w-[calc(100%-140px)] rounded-[40px] btn-primary h-14 md:h-16 font-redit_sans_con font-medium text-xl md:text-28px"
      :class="{
        'opacity-50 cursor-not-allowed': !product.available,
      }"
      @click="addToCartProxy"
    >
      {{ $t("product.addToCart") }}
    </button>
  </div>
</template>
